import { useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

/**
 * RichTextEditor — thin wrapper around Quill (react-quill-new) that
 * gives admins a "real" text editor for email templates. Variable tags
 * like `{{first_name}}` survive Quill's HTML pipeline untouched because
 * they're plain text.
 */
export default function RichTextEditor({ value, onChange, disabled, testid }) {
  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "link"],
      ["clean"],
    ],
    clipboard: { matchVisual: false },
  }), []);
  const formats = ["header","bold","italic","underline","color","background","list","blockquote","link"];

  return (
    <div className="rich-text-wrap rounded-md border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid={testid || "rich-text-editor"}>
      <ReactQuill
        theme="snow"
        value={value || ""}
        onChange={onChange}
        readOnly={disabled}
        modules={modules}
        formats={formats}
        placeholder="Write the email…"
      />
      <style>{`
        .rich-text-wrap .ql-toolbar { background:#121212; border:0; border-bottom:1px solid rgba(255,255,255,0.08); }
        .rich-text-wrap .ql-toolbar .ql-stroke { stroke:#e4e4e7; }
        .rich-text-wrap .ql-toolbar .ql-fill { fill:#e4e4e7; }
        .rich-text-wrap .ql-toolbar .ql-picker-label,
        .rich-text-wrap .ql-toolbar button { color:#e4e4e7; }
        .rich-text-wrap .ql-toolbar .ql-active .ql-stroke,
        .rich-text-wrap .ql-toolbar button:hover .ql-stroke { stroke:#F59E0B; }
        .rich-text-wrap .ql-toolbar .ql-active .ql-fill,
        .rich-text-wrap .ql-toolbar button:hover .ql-fill { fill:#F59E0B; }
        .rich-text-wrap .ql-container { border:0; min-height:260px; color:#e4e4e7; font-family:inherit; font-size:14px; }
        .rich-text-wrap .ql-editor { min-height:260px; background:#0A0A0A; }
        .rich-text-wrap .ql-editor.ql-blank::before { color:#71717a; font-style:normal; }
        .rich-text-wrap .ql-snow .ql-picker-options { background:#121212; border:1px solid rgba(255,255,255,0.08); color:#e4e4e7; }
        .rich-text-wrap .ql-snow .ql-tooltip { background:#121212; color:#e4e4e7; border:1px solid rgba(255,255,255,0.15); }
        .rich-text-wrap .ql-snow .ql-tooltip input { background:#0A0A0A; color:#e4e4e7; border:1px solid rgba(255,255,255,0.15); }
      `}</style>
    </div>
  );
}

/**
 * Read-only renderer for RichTextEditor output. Mirrors the editor's
 * styling so previews and rendered forms look identical to what admins
 * saw in the toolbar. Consumers must guarantee the HTML came from a
 * trusted author (org_admin) — this uses dangerouslySetInnerHTML.
 */
export function RichTextView({ html, className = "" }) {
  return (
    <>
      <div
        className={`ql-editor rich-text-view text-sm leading-relaxed ${className}`}
        style={{ padding: 0, minHeight: 0 }}
        dangerouslySetInnerHTML={{ __html: html || "" }}
      />
      <style>{`
        .rich-text-view a { color: #F59E0B; text-decoration: underline; }
        .rich-text-view p { margin: 0 0 0.5rem; }
        .rich-text-view h2 { font-size: 1.15rem; font-weight: 600; margin: 0.5rem 0; }
        .rich-text-view h3 { font-size: 1.05rem; font-weight: 600; margin: 0.5rem 0; }
        .rich-text-view ul { list-style: disc; padding-left: 1.25rem; }
        .rich-text-view ol { list-style: decimal; padding-left: 1.25rem; }
        .rich-text-view blockquote { border-left: 3px solid #F59E0B; padding-left: 0.75rem; color: #a1a1aa; }
      `}</style>
    </>
  );
}
